import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";

import {
  faArrowRight,
  faArrowLeft,
  faDownload,
  faVrCardboard,
  faLocationDot,
  faBuilding,
  faSun,
  faMoon,
  faChevronDown,
  faPlay,
  faExpand,
  faEnvelope,
  faPhone,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { apiJson } from "../api/apiClient";
import { useSettings } from "../context/SettingsContext";
import "../styles/ProjectLandingPage.css";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Detects a direct video file (mp4/webm/ogg) vs. a hosted-platform link.
const isDirectVideoFile = (url) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");

// Converts a YouTube/Vimeo watch URL into a normal embeddable form — full
// player controls, no forced autoplay/mute/loop, since this plays inline
// in its own section where the visitor is choosing to watch it (not an
// ambient background). Falls back to the raw URL for anything already
// embeddable or unrecognized.
const toEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) return url;
      const videoId = u.searchParams.get("v");
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      if (u.pathname.startsWith("/shorts/")) {
        return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
      }
    }
    if (u.hostname.includes("vimeo.com")) {
      if (u.pathname.startsWith("/video/")) return url;
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  } catch {
    return url;
  }
};

const ProjectLandingPage = () => {
  const { tenant, projectId } = useParams();
  const navigate = useNavigate();
  const { settings, isLoaded: settingsLoaded } = useSettings();

  const [project, setProject] = useState(null);
  const [blockSummary, setBlockSummary] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [heroVisible, setHeroVisible] = useState(false);
  const [galleryIdx, setGalleryIdx] = useState(null);
  const [floorPlanLightbox, setFloorPlanLightbox] = useState(null);
  const [isNight, setIsNight] = useState(false);
  const [activeWalkthroughIdx, setActiveWalkthroughIdx] = useState(0);

  const amenitySwiperRef = useRef(null);

  const sectionRefs = useRef({});

  const withTenant = (path) => `/${tenant}${path}`;
  const setSectionRef = (name) => (el) => {
    if (el) sectionRefs.current[name] = el;
  };

  // ── Helper: build full image URL ────────────────────────────────────
  const getFullImageUrl = useCallback(
    (path) => {
      if (!path) return null;
      if (path.startsWith("http://") || path.startsWith("https://"))
        return path;
      const baseUrl = settings?.img_url || process.env.REACT_APP_IMG_URL || "";
      const cleanPath = path.startsWith("/") ? path.substring(1) : path;
      return baseUrl.endsWith("/")
        ? `${baseUrl}${cleanPath}`
        : `${baseUrl}/${cleanPath}`;
    },
    [settings],
  );

  // ── Fetch project + (optionally) unit stats ─────────────────────────
  useEffect(() => {
    if (!tenant || !projectId) {
      setFetchError("No tenant or project ID provided.");
      setIsFetching(false);
      return;
    }

    const fetchAll = async () => {
      try {
        const data = await apiJson(tenant, `/project/${projectId}`);
        setProject(data);

        if (data?.master_plan_id) {
          try {
            const summary = await apiJson(
              tenant,
              `/blocks/summary/${projectId}/${data.master_plan_id}`,
            );
            setBlockSummary(summary);
          } catch (e) {
            // Non-fatal — the page works fine without unit stats.
          }
        }
      } catch (err) {
        console.error("Project fetch error:", err);
        setFetchError(err.message || "Failed to load this project.");
      } finally {
        setIsFetching(false);
      }
    };

    fetchAll();
  }, [tenant, projectId]);

  useEffect(() => {
    if (isFetching || !project) return;
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, [isFetching, project]);

  // ── GSAP scroll-reveal for every section, once the page has content ──
  useEffect(() => {
    if (!project) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray(".plp-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 44 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    });

    // Layout can shift as images finish loading — keep trigger positions honest.
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener("load", refresh);

    return () => {
      ctx.revert();
      window.removeEventListener("load", refresh);
    };
  }, [project]);

  // ── Smooth-scroll to the first section below the hero ───────────────
  const scrollToNext = () => {
    const target =
      sectionRefs.current.about ||
      sectionRefs.current.amenities ||
      sectionRefs.current.residences;
    if (!target) return;
    gsap.to(window, {
      duration: 1.2,
      ease: "power2.inOut",
      scrollTo: { y: target, offsetY: 0 },
    });
  };

  // ── Loading state ────────────────────────────────────────────────────
  if (isFetching || !settingsLoaded) {
    return (
      <div className="plp-loading">
        <div className="plp-loading-card">
          {settings?.logo_dark_url ? (
            <img
              src={settings.logo_dark_url}
              alt=""
              className="plp-loading-logo"
            />
          ) : (
            <div className="plp-loading-mark">
              {(settings?.company_name || "")[0] || ""}
            </div>
          )}
          <div className="plp-loading-rule">
            <div className="plp-loading-rule__fill" />
          </div>
          <div className="plp-loading-label">Preparing this residence</div>
        </div>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────
  if (fetchError || !project) {
    return (
      <div className="plp-error">
        <div className="plp-error-card">
          <FontAwesomeIcon icon={faBuilding} className="plp-error-icon" />
          <h1>We couldn't load this project</h1>
          <p>{fetchError || "This project may no longer be available."}</p>
          <button
            onClick={() => navigate(withTenant(""))}
            className="plp-error-btn"
          >
            ← Back to all residences
          </button>
        </div>
      </div>
    );
  }

  const amenities = project.amenities || [];
  const gallery = project.gallery_images || [];
  const walkthroughs = project.walkthroughs || [];
  const virtualTours = project.virtual_tours || [];
  const brochures = project.brochures || [];
  const primaryBrochure = brochures[0];
  const hasUnitStats = !!blockSummary?.totals;
  const tenantContact = project.tenant_contact || null;

  // One representative floor plan per unit type (first one found for that
  // type), so the grid shows "1BHK / 2BHK / ..." rather than every unit.
  const floorPlansByType = Object.values(
    (project.floor_plans || []).reduce((acc, fp) => {
      if (fp.plot_type && !acc[fp.plot_type]) acc[fp.plot_type] = fp;
      return acc;
    }, {}),
  );

  // Prefers a real night-render image if the API ever provides one
  // (e.g. project.project_image_night_url); otherwise the .is-night CSS
  // class color-grades the same daytime image into a night mood.
  const heroImageSrc =
    (isNight && project.project_image_night_url) || project.project_image_url;

  const toEmbedUrl = (url) => {
    if (!url) return "";

    let videoId = "";

    // youtube.com/watch?v=
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) {
      videoId = watchMatch[1];
    }

    // youtu.be/
    const shortMatch = url.match(/youtu\.be\/([^?]+)/);
    if (!videoId && shortMatch) {
      videoId = shortMatch[1];
    }

    // Already embed URL
    const embedMatch = url.match(/embed\/([^?]+)/);
    if (!videoId && embedMatch) {
      videoId = embedMatch[1];
    }

    if (!videoId) return url;

    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`;
  };

  const activeWalkthrough =
    walkthroughs[activeWalkthroughIdx] || walkthroughs[0] || null;
  const activeWalkthroughEmbedUrl = activeWalkthrough
    ? toEmbedUrl(activeWalkthrough.walkthrough_url)
    : null;
  const activeWalkthroughIsFile = isDirectVideoFile(
    activeWalkthrough?.walkthrough_url,
  );

  return (
    <div className="plp-page">
      {/* ═══ HERO — unchanged: static image, day/night toggle ═══ */}
      <header className={`plp-hero ${isNight ? "is-night" : ""}`}>
        <div className="plp-hero__media">
          <img
            src={heroImageSrc}
            alt={project.project_name}
            className="plp-hero__img"
          />
          <div className="plp-hero__scrim" />
        </div>

        {/* Day / Night toggle */}
        <div className="plp-daynight">
          <button
            className={`plp-daynight__opt ${!isNight ? "is-active" : ""}`}
            onClick={() => setIsNight(false)}
            aria-label="Day view"
          >
            <FontAwesomeIcon icon={faSun} />
            Day
          </button>
          <button
            className={`plp-daynight__opt ${isNight ? "is-active" : ""}`}
            onClick={() => setIsNight(true)}
            aria-label="Night view"
          >
            <FontAwesomeIcon icon={faMoon} />
            Night
          </button>
        </div>

        <div className={`plp-hero__content ${heroVisible ? "is-visible" : ""}`}>
          {project.logo_url && (
            <img src={project.logo_url} alt="" className="plp-hero__logo" />
          )}

          <div className="plp-eyebrow">
            <span className="plp-eyebrow__line" />
            <span>{project.location || "A New Address"}</span>
          </div>

          <h1 className="plp-hero__title">
            {project.tagline || project.project_name}
            {project.tagline_italic && <em>{project.tagline_italic}</em>}
          </h1>

          {project.description && (
            <p className="plp-hero__desc">
              {project.description.length > 180
                ? project.description.slice(0, 180).trim() + "…"
                : project.description}
            </p>
          )}

          <div className="plp-hero__actions">
            <button
              className="plp-btn plp-btn--solid"
              onClick={() =>
                project.master_plan_id
                  ? navigate(
                      withTenant(`/apartment-view/${project.master_plan_id}`),
                    )
                  : navigate(withTenant(`/location/${project.id}`))
              }
            >
              Explore Residences
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            {primaryBrochure && (
              <a
                href={primaryBrochure.brochure_url}
                target="_blank"
                rel="noreferrer"
                className="plp-btn plp-btn--ghost"
              >
                <FontAwesomeIcon icon={faDownload} />
                Download Brochure
              </a>
            )}
          </div>
        </div>

        <button
          className="plp-hero__scroll"
          onClick={scrollToNext}
          aria-label="Scroll down"
        >
          <span className="plp-hero__scroll-line" />
          Scroll to discover
          <FontAwesomeIcon
            icon={faChevronDown}
            className="plp-hero__scroll-chevron"
          />
        </button>
      </header>

      {/* ═══ ABOUT ═══ */}
      {project.description && (
        <section className="plp-about plp-reveal" ref={setSectionRef("about")}>
          <div className="plp-about__media">
            <img src={project.project_image_url} alt="" />
          </div>
          <div className="plp-about__content">
            <div className="plp-eyebrow">
              <span className="plp-eyebrow__line" />
              <span>About the Residence</span>
            </div>
            <h2 className="plp-section-title">
              A place built <em>around life</em>
            </h2>
            <p className="plp-about__text">{project.description}</p>
          </div>
        </section>
      )}

      {/* ═══ WALKTHROUGH — full-view inline player, its own section, no modal ═══ */}
      {walkthroughs.length > 0 && (
        <section
          className="plp-walkthrough plp-reveal"
          ref={setSectionRef("walkthrough")}
        >
          <div className="plp-eyebrow plp-eyebrow--center">
            <span className="plp-eyebrow__line" />
            <span>Walkthrough</span>
            <span className="plp-eyebrow__line" />
          </div>
          <h2 className="plp-section-title plp-section-title--center">
            Take a <em>closer look</em>
          </h2>

          <div className="plp-walkthrough__player">
            {/* {!activeWalkthroughIsFile && activeWalkthroughEmbedUrl && ( */}
            <iframe
              key={activeWalkthroughEmbedUrl}
              src={activeWalkthroughEmbedUrl}
              title={activeWalkthrough?.title || "Walkthrough"}
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              frameBorder="0"
              style={{
                width: "100%",
                aspectRatio: "16 / 9",
                border: 0,
              }}
            />
            {/* )} */}
          </div>

          {/* {walkthroughs.length > 1 && (
                  <div className="plp-walkthrough__tabs">
                    {walkthroughs.map((w, i) => (
                      <button
                        key={i}
                        className={`plp-walkthrough__tab ${i === activeWalkthroughIdx ? "is-active" : ""}`}
                        onClick={() => setActiveWalkthroughIdx(i)}
                      >
                        <FontAwesomeIcon icon={faPlay} />
                        {w.title || `Walkthrough ${i + 1}`}
                      </button>
                    ))}
                  </div>
                )} */}
        </section>
      )}

      {/* ═══ AMENITIES ═══ */}
      {amenities.length > 0 && (
        <section
          className="plp-amenities plp-reveal"
          ref={setSectionRef("amenities")}
        >
          <div className="plp-section-head">
            <div>
              <div className="plp-eyebrow">
                <span className="plp-eyebrow__line" />
                <span>Amenities</span>
              </div>
              <h2 className="plp-section-title">
                Everyday living, <em>elevated</em>
              </h2>
            </div>
            <div className="plp-carousel-nav">
              <button
                onClick={() => amenitySwiperRef.current?.slidePrev()}
                aria-label="Previous amenity"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <button
                onClick={() => amenitySwiperRef.current?.slideNext()}
                aria-label="Next amenity"
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>

          <Swiper
            modules={[Autoplay, Navigation, FreeMode]}
            onSwiper={(swiper) => (amenitySwiperRef.current = swiper)}
            className="plp-amenity-strip"
            spaceBetween={24}
            slidesPerView={3}
            speed={800}
            loop={amenities.length > 3}
            grabCursor={true}
            freeMode={{
              enabled: true,
              sticky: true,
              momentum: true,
            }}
            autoplay={{
              delay: 2800,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            breakpoints={{
              640: { slidesPerView: 2, spaceBetween: 24 },
              1024: { slidesPerView: 3, spaceBetween: 24 },
            }}
          >
            {amenities.map((a, i) => {
              const img = getFullImageUrl(
                a.amenity_image_url || a.amenity_icon_url,
              );
              return (
                <SwiperSlide key={i} className="plp-amenity-card">
                  <div className="plp-amenity-card__media">
                    {img ? (
                      <img src={img} alt={a.amenity_name} />
                    ) : (
                      <div className="plp-amenity-card__placeholder">
                        <FontAwesomeIcon icon={faBuilding} />
                      </div>
                    )}
                  </div>
                  <div className="plp-amenity-card__label">
                    {a.amenity_name}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </section>
      )}

      {/* ═══ GALLERY ═══ */}
      {gallery.length > 0 && (
        <section className="plp-gallery plp-reveal">
          <div className="plp-eyebrow plp-eyebrow--center">
            <span className="plp-eyebrow__line" />
            <span>Gallery</span>
            <span className="plp-eyebrow__line" />
          </div>
          <h2 className="plp-section-title plp-section-title--center">
            Everyday <em>views</em>
          </h2>

          <div className="plp-gallery__grid">
            {gallery.slice(0, 6).map((img, i) => (
              <div
                key={i}
                className={`plp-gallery__item ${i % 3 === 1 ? "is-tall" : ""}`}
                onClick={() => setGalleryIdx(i)}
              >
                <img src={getFullImageUrl(img)} alt="" />
              </div>
            ))}
          </div>
        </section>
      )}

      {galleryIdx !== null && gallery[galleryIdx] && (
        <div className="plp-lightbox" onClick={() => setGalleryIdx(null)}>
          <img src={getFullImageUrl(gallery[galleryIdx])} alt="" />
        </div>
      )}

      {/* ═══ RESIDENCES / UNITS ═══ */}
      <section
        className="plp-residences floor-plan-section plp-reveal"
        ref={setSectionRef("residences")}
      >
        <div className="plp-eyebrow">
          <span className="plp-eyebrow__line" />
          <span>Residences</span>
        </div>
        <h2 className="plp-section-title">
          Find your <em>floor plan</em>
        </h2>

        {hasUnitStats ? (
          <div className="plp-residences__stats">
            <div className="plp-stat">
              <div className="plp-stat__value">
                {blockSummary.totals.total_plots}
              </div>
              <div className="plp-stat__label">Total Units</div>
            </div>
            <span className="plp-stat__divider" />
            <div className="plp-stat plp-stat--available">
              <div className="plp-stat__value">
                {blockSummary.totals.available_plot_count}
              </div>
              <div className="plp-stat__label">Available Now</div>
            </div>
            <span className="plp-stat__divider" />
            <div className="plp-stat plp-stat--sold">
              <div className="plp-stat__value">
                {blockSummary.totals.sold_plot_count}
              </div>
              <div className="plp-stat__label">Sold</div>
            </div>
          </div>
        ) : (
          <p className="plp-residences__note">
            Explore the interactive 360° experience to browse every available
            unit, floor by floor.
          </p>
        )}

        {floorPlansByType.length > 0 && (
          <div className="plp-floorplans">
            <div className="plp-floorplans__label">Available Layouts</div>
            <div className="plp-floorplans__grid">
              {floorPlansByType.map((fp) => (
                <button
                  key={fp.plot_type}
                  className="plp-floorplan-card"
                  onClick={() => setFloorPlanLightbox(fp.floor_plan_url)}
                >
                  <div className="plp-floorplan-card__media">
                    <img src={fp.floor_plan_url} alt={fp.plot_type} />
                    <div className="plp-floorplan-card__overlay">
                      <FontAwesomeIcon icon={faExpand} />
                      View Floor Plan
                    </div>
                  </div>
                  <div className="plp-floorplan-card__label">
                    {fp.plot_type}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="plp-hero__actions" style={{ marginTop: 32 }}>
          <button
            className="plp-btn plp-btn--solid"
            onClick={() =>
              project.master_plan_id
                ? navigate(
                    withTenant(`/apartment-view/${project.master_plan_id}`),
                  )
                : navigate(withTenant(`/location/${project.id}`))
            }
          >
            Explore Units in 360°
            <FontAwesomeIcon icon={faVrCardboard} />
          </button>
          {primaryBrochure && (
            <a
              href={primaryBrochure.brochure_url}
              target="_blank"
              rel="noreferrer"
              className="plp-btn plp-btn--ghost"
            >
              <FontAwesomeIcon icon={faDownload} />
              Download Floor Plans
            </a>
          )}
        </div>
      </section>

      {floorPlanLightbox && (
        <div
          className="plp-lightbox"
          onClick={() => setFloorPlanLightbox(null)}
        >
          <img src={floorPlanLightbox} alt="Floor plan" />
        </div>
      )}

      {/* ═══ VIRTUAL TOURS — 360° tours only; walkthroughs already have
          their own full-view section above ═══ */}
      {virtualTours.length > 0 && (
        <section className="plp-tours plp-reveal">
          <div className="plp-eyebrow">
            <span className="plp-eyebrow__line" />
            <span>Experience It</span>
          </div>
          <h2 className="plp-section-title">
            Explore in <em>360°</em>
          </h2>

          <div className="plp-tours__grid">
            {virtualTours.map((v, i) => (
              <a
                key={`v-${i}`}
                href={v.tour_url}
                target="_blank"
                rel="noreferrer"
                className="plp-tour-card"
              >
                <FontAwesomeIcon
                  icon={faVrCardboard}
                  className="plp-tour-card__icon"
                />
                <div>
                  <div className="plp-tour-card__type">360° Tour</div>
                  <div className="plp-tour-card__title">
                    {v.title || `Tour ${i + 1}`}
                  </div>
                </div>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="plp-tour-card__arrow"
                />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* ═══ LOCATION ═══ */}
      {project.location && (
        <section className="plp-location plp-reveal">
          <div className="plp-eyebrow">
            <span className="plp-eyebrow__line" />
            <span>Location</span>
          </div>
          <h2 className="plp-section-title">
            Positioned <em>perfectly</em>
          </h2>

          <div className="plp-location__grid">
            {/* Left — full map */}
            <div className="plp-location__map">
              <iframe
                src={`https://www.google.com/maps?q=${encodeURIComponent(project.location)}&output=embed`}
                title="Project location"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Right — contact details */}
            <div className="plp-location__contact">
              <div className="plp-location__row">
                <span className="plp-location__icon">
                  <FontAwesomeIcon icon={faLocationDot} />
                </span>
                <div>
                  <div className="plp-location__label">Address</div>
                  <div className="plp-location__value">                   
                    {tenantContact?.address_line1 && (
                      <>                       
                        {tenantContact.address_line1}
                      </>
                    )}
                    {tenantContact?.address_line2 && (
                      <>
                        <br />
                        {tenantContact.address_line2}
                      </>
                    )}
                    {(tenantContact?.city ||
                      tenantContact?.state ||
                      tenantContact?.country ||
                      tenantContact?.postal_code) && (
                      <>
                        <br />
                        {[
                          tenantContact?.city,
                          tenantContact?.state,
                          tenantContact?.country,
                          tenantContact?.postal_code,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {tenantContact?.email && (
                <a
                  href={`mailto:${tenantContact.email}`}
                  className="plp-location__row plp-location__row--link"
                >
                  <span className="plp-location__icon">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </span>
                  <div>
                    <div className="plp-location__label">Email</div>
                    <div className="plp-location__value">
                      {tenantContact.email}
                    </div>
                  </div>
                </a>
              )}

              {tenantContact?.mobile && (
                <a
                  href={`tel:${tenantContact.mobile}`}
                  className="plp-location__row plp-location__row--link"
                >
                  <span className="plp-location__icon">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <div>
                    <div className="plp-location__label">Phone</div>
                    <div className="plp-location__value">
                      {tenantContact.mobile}
                    </div>
                  </div>
                </a>
              )}

              {tenantContact?.website && (
                <a
                  href={tenantContact.website}
                  target="_blank"
                  rel="noreferrer"
                  className="plp-location__row plp-location__row--link"
                >
                  <span className="plp-location__icon">
                    <FontAwesomeIcon icon={faGlobe} />
                  </span>
                  <div>
                    <div className="plp-location__label">Website</div>
                    <div className="plp-location__value">
                      {tenantContact.website.replace(/^https?:\/\//, "")}
                    </div>
                  </div>
                </a>
              )}

              <button
                className="plp-btn plp-btn--solid"
                style={{ marginTop: 8 }}
                onClick={() => navigate(withTenant(`/location/${project.id}`))}
              >
                Get Directions
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ═══ FOOTER CTA — no form, just the two real actions ═══ */}
      <section className="plp-footer-cta plp-reveal">
        <h2 className="plp-section-title plp-section-title--center">
          Ready to see <em>{project.project_name}</em> for yourself?
        </h2>
        <div className="plp-hero__actions" style={{ justifyContent: "center" }}>
          <button
            className="plp-btn plp-btn--solid"
            onClick={() =>
              project.master_plan_id
                ? navigate(
                    withTenant(`/apartment-view/${project.master_plan_id}`),
                  )
                : navigate(withTenant(`/location/${project.id}`))
            }
          >
            Explore Residences
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          {primaryBrochure && (
            <a
              href={primaryBrochure.brochure_url}
              target="_blank"
              rel="noreferrer"
              className="plp-btn plp-btn--ghost"
            >
              <FontAwesomeIcon icon={faDownload} />
              Download Brochure
            </a>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProjectLandingPage;
